import React from 'react';
import { ChevronDown, Search, Plus } from 'lucide-react';
import {
    FAMILLES, ORDRE_FAMILLES, JETONS_BOUTON_OU_LIEN, construireVariables,
    type FamilleVariable, type VariableAffichee,
} from '../../lib/variablesEmail';

/**
 * « Insérer une variable » : les informations qui se remplissent toutes seules dans un e-mail
 * (nom du client, date, montant…), rangées par famille avec un exemple sous chacune.
 *
 * Remplace le nuage de petits boutons à plat, où « Nom client » et « Nom salarié assigné » se
 * ressemblaient et où l'on ne voyait pas ce que la variable donnerait. Le texte inséré reste le même
 * jeton `{{…}}` : aucun modèle déjà enregistré n'est touché.
 *
 * Pour un élément cliquable (paiement, avis, annulation…), le choix « bouton » ou « lien dans le
 * texte » se fait à l'insertion : `{{lien_paiement}}` = bouton, `{{lien_paiement:lien}}` = lien.
 */

const nettoyer = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function SelecteurVariables({ tokens, onInsertToken, integre = false, seulementEcran = false }: {
    /** Ne montre que les variables de l'écran, sous le libellé qu'il leur donne (voir construireVariables). */
    seulementEcran?: boolean;
    /** Posé dans un volet à côté du texte : toujours ouvert, sans en-tête repliable. */
    integre?: boolean;
    /** Les variables que SAIT remplir l'e-mail ouvert (liste propre à chaque écran). */
    tokens: { token: string; label: string }[];
    onInsertToken: (token: string) => void;
}) {
    const [ouvertManuel, setOuvert] = React.useState(false);
    const ouvert = integre || ouvertManuel;
    const [recherche, setRecherche] = React.useState('');
    const [famille, setFamille] = React.useState<FamilleVariable | 'toutes'>('toutes');
    const [derniere, setDerniere] = React.useState('');

    const variables = React.useMemo(() => construireVariables(tokens, { seulementEcran }), [tokens, seulementEcran]);
    const nbDispo = variables.filter((v) => v.disponible).length;
    const familles = ORDRE_FAMILLES.filter((f) => variables.some((v) => v.famille === f));

    const q = nettoyer(recherche.trim());
    const visibles = variables.filter((v) => {
        if (famille !== 'toutes' && v.famille !== famille) return false;
        if (!q) return true;
        return nettoyer(`${v.nom} ${FAMILLES[v.famille].nom} ${v.exemple}`).includes(q);
    });

    const inserer = (v: VariableAffichee, lien = false) => {
        onInsertToken(lien ? `{{${v.code}:lien}}` : `{{${v.code}}}`);
        setDerniere(`« ${v.nom} » ajouté`);
    };
    // Garde le curseur dans le champ : un clic sur le panneau ne doit pas retirer le focus du texte.
    const garderFocus = (e: React.MouseEvent) => e.preventDefault();

    return (
        <div className="rounded-lg border border-border bg-surface">
            {!integre && (
                <button type="button" onClick={() => setOuvert((o) => !o)} aria-expanded={ouvert}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left">
                    <span className="text-[12.5px] font-semibold text-content">
                        Insérer une variable <span className="font-normal text-content-muted">(nom du client, date, montant…)</span>
                    </span>
                    <ChevronDown size={14} className={`text-content-muted shrink-0 transition-transform ${ouvert ? 'rotate-180' : ''}`} />
                </button>
            )}

            {ouvert && (
                <div className={integre ? '' : 'border-t border-border'}>
                    <div className="p-3 space-y-2.5">
                        <p className="text-[11.5px] text-content-muted">
                            Le curseur reste là où vous écriviez : cliquez sur une information, elle s'y glisse.
                            Celles qui sont grisées n'existent pas pour cet e-mail ({nbDispo} utilisables).
                        </p>
                        <label className="relative block">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted pointer-events-none" />
                            <input type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)}
                                placeholder="Rechercher : date, client, montant…" aria-label="Rechercher une variable"
                                className="w-full h-9 bg-surface-soft border border-border rounded-lg pl-9 pr-3 text-[13px] text-content outline-none focus:border-brand-500" />
                        </label>
                        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrer par famille">
                            <button type="button" aria-pressed={famille === 'toutes'} onClick={() => setFamille('toutes')}
                                className={`px-2.5 py-1 max-lg:min-h-[44px] rounded-full border text-[12px] font-semibold ${famille === 'toutes' ? 'border-content text-content bg-surface-soft' : 'border-border text-content-muted'}`}>
                                Tout
                            </button>
                            {familles.map((f) => (
                                <button key={f} type="button" aria-pressed={famille === f} onClick={() => setFamille(f)}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 max-lg:min-h-[44px] rounded-full border text-[12px] font-semibold ${famille === f ? 'border-content text-content bg-surface-soft' : 'border-border text-content-muted'}`}>
                                    <span className="w-[7px] h-[7px] rounded-full" style={{ background: FAMILLES[f].couleur }} />
                                    {FAMILLES[f].nom}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={`${integre ? 'max-h-[calc(100vh-380px)] min-h-[200px]' : 'max-h-[340px]'} overflow-y-auto px-2 pb-3`} onMouseDown={garderFocus}>
                        {visibles.length === 0 && (
                            <p className="px-3 py-6 text-center text-[13px] text-content-muted">
                                Aucune variable ne correspond à « {recherche} ». Essayez « date », « client » ou « montant ».
                            </p>
                        )}
                        {ORDRE_FAMILLES.map((f) => {
                            const lignes = visibles.filter((v) => v.famille === f);
                            if (!lignes.length) return null;
                            const info = FAMILLES[f];
                            return (
                                <section key={f} className="mt-2">
                                    <div className="flex items-center gap-2 px-2 pt-2 pb-0.5 text-[11px] font-bold uppercase tracking-[.09em]" style={{ color: info.couleur }}>
                                        <span className="w-2 h-2 rounded-full" style={{ background: info.couleur }} />
                                        {info.nom}
                                        <span className="ml-auto font-semibold normal-case tracking-normal text-content-muted">{lignes.length}</span>
                                    </div>
                                    <p className="px-2 pb-1 text-[11.5px] text-content-muted">{info.aide}</p>
                                    {lignes.map((v) => (
                                        <LigneVariable key={v.code} v={v} couleur={info.couleur} onInsert={inserer} lienPossible={!seulementEcran} />
                                    ))}
                                </section>
                            );
                        })}
                    </div>
                    <p className="px-3 py-1.5 border-t border-border text-[11.5px] text-content-muted min-h-[28px]" role="status" aria-live="polite">{derniere}</p>
                </div>
            )}
        </div>
    );
}

function LigneVariable({ v, couleur, onInsert, lienPossible }: {
    v: VariableAffichee;
    couleur: string;
    /** Le choix « lien » n'est offert que là où l'envoi sait le traiter (e-mails de Notifications). */
    lienPossible: boolean;
    onInsert: (v: VariableAffichee, lien?: boolean) => void;
}) {
    const etiquetteVide = v.videSi ? (
        <span title={`Reste vide si ${v.videSi}`}
            className="ml-1.5 align-[1px] text-[10.5px] font-bold px-1.5 py-px rounded-md" style={{ background: 'var(--st-warn-bg)', color: 'var(--st-warn-fg)' }}>
            peut être vide
        </span>
    ) : null;
    const code = `{{${v.code}}}`;

    if (!v.disponible) {
        return (
            <div className="flex items-center gap-2 px-2.5 py-1.5 max-lg:min-h-[44px] rounded-lg opacity-50 cursor-not-allowed" title="Cette information n'existe pas pour cet e-mail">
                <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold text-content">{v.nom}</span>
                    <span className="block text-[12px] text-content-muted">Pas utilisable dans cet e-mail</span>
                </span>
            </div>
        );
    }

    if (v.cliquable) {
        const peutLien = lienPossible && JETONS_BOUTON_OU_LIEN.has(v.code);
        return (
            <div className="flex items-center gap-2 px-2.5 py-1.5 max-lg:min-h-[44px] rounded-lg hover:bg-surface-soft" title={`Code : ${code}`}>
                <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold text-content">{lienPossible ? `« ${v.nom} »` : v.nom}{etiquetteVide}</span>
                    <span className="block text-[12px] text-content-muted">S'affiche au choix :</span>
                </span>
                <span className="flex gap-1 shrink-0">
                    <button type="button" onClick={() => onInsert(v)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 max-lg:min-h-[44px] rounded-lg border border-border bg-surface text-[12px] font-semibold text-content hover:border-content">
                        <i className="inline-block w-3.5 h-2 rounded-[3px]" style={{ background: couleur }} />Bouton
                    </button>
                    {peutLien && (
                        <button type="button" onClick={() => onInsert(v, true)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 max-lg:min-h-[44px] rounded-lg border border-border bg-surface text-[12px] font-semibold text-content hover:border-content">
                            <i className="not-italic underline text-[10.5px]" style={{ color: couleur }}>abc</i>Lien
                        </button>
                    )}
                </span>
            </div>
        );
    }

    return (
        <button type="button" onClick={() => onInsert(v)} title={`Code : ${code}`}
            className="group w-full flex items-center gap-2 px-2.5 py-1.5 max-lg:min-h-[44px] rounded-lg text-left hover:bg-surface-soft focus-visible:bg-surface-soft outline-none">
            <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-content">{v.nom}{etiquetteVide}</span>
                {(v.image || v.exemple) && (
                    <span className="block text-[12px] text-content-muted truncate">{v.image ? 'Image' : `Exemple : ${v.exemple}`}</span>
                )}
            </span>
            <span className="w-[22px] h-[22px] rounded-full grid place-items-center text-white shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" style={{ background: couleur }} aria-hidden="true">
                <Plus size={13} />
            </span>
        </button>
    );
}
